/*
  Wire payloads → the view shapes the drill-down components render.

  Everything the contract says about *presentation* is enforced here, in one
  place, so no component has to remember it:

    · null means unknown, never zero → "n/a", never "0"
    · filter values go back verbatim, never trimmed
    · `sync_healthy: false` earns a stale badge
    · 502/503/504 are states the screen renders, not errors to swallow

  Keeping it in a module of its own also means the whole translation is unit
  testable without mounting a page.
*/

import { ApiError } from './api/caller'
import type {
  DeepDiveStatus,
  FilterCatalogue,
  FilterDimension,
  IncidentRow,
  QuarterRow,
  SalesFilterKey,
  SalesFilterSelection,
  SalesPeriodSelection,
  SalesCoverage,
  SalesOverview,
  SalesSeries,
  SalesSummary,
  SalesTimelinePayload,
  ServiceOverview,
} from './api/deep-dive.types'
import { SALES_FILTER_KEYS } from './api/deep-dive.types'
import {
  ALL_OPTION,
  PERIOD_LABELS,
  type FilterGroup,
  type Freshness,
  type Incident,
  type IncidentStatus,
  type QuarterPoint,
  type StatTileData,
  type TimelinePoint,
} from './system-intelligence'

/* ------------------------------------------------------------- null → "n/a" */

/**
 * The contract's central rule in one function: a missing number reads "n/a", and
 * only a number the backend actually reported is allowed to render as 0.
 */
export function naNumber(
  value: number | null | undefined,
  format: (n: number) => string = (n) => n.toLocaleString(),
): string {
  return typeof value === 'number' && Number.isFinite(value) ? format(value) : 'n/a'
}

/** "100%", "92.8%" — one decimal, dropped when the value is whole. */
export function percent(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits))
  return `${rounded}%`
}

export const naPercent = (value: number | null | undefined) => naNumber(value, (n) => percent(n))
export const naMs = (value: number | null | undefined) => naNumber(value, (n) => `${n}ms`)

/* --------------------------------------------------- upstream failure states */

/** What a screen (or one panel of it) shows in place of data it could not get. */
export interface DataSourceState {
  /** The line the panel leads with. */
  headline: string
  /** The backend's own wording, when it sent any. */
  detail?: string
  /** True when retrying is the sensible next move (slow upstream, network). */
  retryable: boolean
}

/**
 * Map a failed call onto the UI wording the contract prescribes.
 *
 * The four upstream codes are deliberately *not* collapsed into "something went
 * wrong": 503 means the source was never connected (nothing to retry — the
 * DocuSign envelope routes sit here until OWEHub ships), while 504 means it was
 * simply slow, which is worth another go. Telling an on-call engineer which of
 * the two they are looking at is most of this function's value.
 */
export function dataSourceState(error: unknown): DataSourceState {
  const api = error instanceof ApiError ? error : null
  const detail = api?.message

  switch (api?.status) {
    case 503:
      return { headline: 'Data source not connected', detail, retryable: false }
    case 504:
      return { headline: 'Data source slow, retry', detail, retryable: true }
    case 502:
      return { headline: 'Data unavailable', detail, retryable: true }
    case 400:
      // A bad window/date/platform is our bug, not the upstream's — say so
      // plainly rather than blaming the data source.
      return { headline: 'The request was rejected', detail, retryable: false }
    case 401:
    case 403:
      return { headline: 'You are not signed in for this', detail, retryable: false }
  }

  if (api?.code === 'timeout')
    return { headline: 'Data source slow, retry', detail, retryable: true }
  if (api?.code === 'network')
    return { headline: 'Cannot reach the service', detail, retryable: true }

  return { headline: 'Data unavailable', detail, retryable: true }
}

/* --------------------------------------------------------------- freshness */

/** Pull the stale badge's inputs off any OWEHub-backed payload. */
export function toFreshness(payload: {
  data_as_of?: string | null
  sync_healthy?: boolean
}): Freshness {
  return {
    dataAsOf: payload.data_as_of ?? null,
    // Absent means the payload carries no sync concept (One Portal, and the
    // health routes) — that is not the same as a failing sync.
    syncHealthy: payload.sync_healthy !== false,
  }
}

/* ------------------------------------------------------------------ health */

/** Server status → the tone a headline figure is painted in. */
function statusTone(status: DeepDiveStatus): StatTileData['tone'] {
  switch (status) {
    case 'healthy':
      return 'healthy'
    case 'minor':
    case 'major':
    case 'maintenance':
      return 'degraded'
    case 'critical':
    case 'vendor_silent':
      return 'critical'
    default:
      return 'neutral'
  }
}

/** The two health tiles: response time, and 30-day uptime with its coverage. */
export function toHealthTiles(
  overview: ServiceOverview | undefined,
  incidentCount: number | undefined,
): StatTileData[] {
  const tone = overview ? statusTone(overview.status) : 'neutral'
  const uptime = overview?.uptime_30d

  const delta =
    incidentCount === undefined || incidentCount === 0
      ? undefined
      : {
          value: String(incidentCount).padStart(2, '0'),
          direction: 'down' as const,
          title: `${incidentCount} status ${incidentCount === 1 ? 'change' : 'changes'} in the last 30 days`,
        }

  return [
    {
      id: 'response-time',
      label: 'Response Time',
      value: naMs(overview?.response_time_ms),
      tone,
      delta,
    },
    {
      id: 'uptime',
      label: '30-Day Uptime',
      value: naPercent(uptime?.uptime_pct),
      // Uptime without coverage is a half-truth: 100% over 8% of the window is
      // not the same claim as 100% over all of it, so the coverage rides along.
      note:
        typeof uptime?.known_coverage_pct === 'number'
          ? `${percent(uptime.known_coverage_pct)} observed`
          : undefined,
      tone:
        typeof uptime?.uptime_pct !== 'number'
          ? 'neutral'
          : uptime.uptime_pct >= 99.9
            ? 'healthy'
            : uptime.uptime_pct >= 99
              ? 'degraded'
              : 'critical',
      delta,
    },
  ]
}

/**
 * The sales tile: today's count against the expected band.
 *
 * "insufficient history" is a real answer, not a zero — the contract is explicit
 * that it must read "no baseline" rather than "Exp. 0-0", because a band of
 * nothing looks like a target the team missed.
 */
export function toSalesTile(
  sales: SalesOverview | undefined,
  unit: string,
  /** False once a past day has been picked — the tile stops saying "Today". */
  isToday = true,
): StatTileData {
  const expected = sales?.expected
  const hasBand = typeof expected?.min === 'number' && typeof expected?.max === 'number'

  return {
    id: 'sales-today',
    // A tile labelled "Sales Today" showing a figure from three weeks ago is the
    // kind of wrong that gets acted on, so the label follows the chosen day.
    label: isToday ? `${unit} Today` : `${unit} · ${sales?.date ?? 'selected day'}`,
    value: naNumber(sales?.sales_today),
    note: hasBand ? `/ Exp. ${expected.min}-${expected.max}` : sales ? '/ no baseline' : undefined,
    tone:
      !hasBand || typeof sales?.sales_today !== 'number'
        ? 'neutral'
        : sales.sales_today >= (expected.min ?? 0)
          ? 'healthy'
          : 'degraded',
    delta: sales?.cancelled_today
      ? {
          value: String(sales.cancelled_today).padStart(2, '0'),
          direction: 'down',
          title: `${sales.cancelled_today} cancelled today`,
        }
      : undefined,
  }
}

/* -------------------------------------------------------------- incidents */

/** "18m 04s", "2h 05m 11s", or "Ongoing" while the incident is still open. */
export function formatIncidentDuration(ms: number | null, ongoing: boolean): string {
  if (ongoing || ms === null) return 'Ongoing'
  const total = Math.max(0, Math.round(ms / 1000))
  const pad = (n: number) => String(n).padStart(2, '0')
  const hours = Math.floor(total / 3600)
  const mins = Math.floor((total % 3600) / 60)
  const secs = total % 60
  return hours > 0 ? `${hours}h ${pad(mins)}m ${pad(secs)}s` : `${mins}m ${pad(secs)}s`
}

/**
 * Incident rows for the table. Stamped in UTC on purpose: an incident timeline
 * anyone might read from another timezone has to line up with the vendor's own
 * console.
 */
export function toIncidents(rows: IncidentRow[] | undefined): Incident[] {
  return (rows ?? []).map((row) => ({
    id: String(row.id),
    date: row.started_at.slice(0, 19).replace('T', ' '),
    from: row.from_status as IncidentStatus,
    to: row.to_status as IncidentStatus,
    duration: formatIncidentDuration(row.duration_ms, row.ongoing),
    indicator: row.indicator,
    ongoing: row.ongoing,
  }))
}

/* --------------------------------------------------------------- quarterly */

export function toQuarterPoints(rows: QuarterRow[] | undefined): QuarterPoint[] {
  return (rows ?? []).map((row) => ({
    quarter: row.quarter,
    sales: row.sales_count,
    cancelled: row.cancelled_count,
    kw: row.kw_total,
    contractValue: row.contract_value_total,
  }))
}

/* ---------------------------------------------------------------- timeline */

export function toTimelinePoints(payload: SalesTimelinePayload | undefined): TimelinePoint[] {
  return (payload?.points ?? []).map((p) => ({
    label: `${String(p.hour).padStart(2, '0')}:00`,
    actual: p.actual,
    baseline: p.baseline,
    cumulativeActual: p.cumulative_actual,
    cumulativeBaseline: p.cumulative_baseline,
  }))
}

/* ----------------------------------------------------------------- filters */

const FILTER_LABELS: Record<SalesFilterKey, string> = {
  state: 'State',
  office: 'Office',
  dealer: 'Dealer',
  partner: 'Partner',
  finance: 'Finance',
  region: 'Region',
}

/** How many values a dimension may offer before it is not worth a dropdown. */
const MAX_OPTIONS = 300

/**
 * The filter catalogue → the scope row's dropdowns.
 *
 * Two upstream shapes arrive here (see `FilterCatalogue`) and both are passed
 * through verbatim: values can carry trailing spaces and are matched exactly and
 * case-sensitively, so nothing is trimmed, sorted by name or prettified.
 *
 * A dimension that is almost entirely blank is dropped rather than shown empty —
 * `region` is ~99% blank on Aurora and Solo, and a chip whose every option
 * matches four rows is a trap, not a filter. Upstream's own note comes along for
 * the ones that survive.
 */
export function toFilterGroups(catalogue: FilterCatalogue | undefined): FilterGroup[] {
  if (!catalogue) return []

  if (catalogue.platform === 'one_portal') {
    const v = catalogue.values
    const flat: Partial<Record<SalesFilterKey, string[] | undefined>> = {
      // One Portal treats state and region as the same dimension, and rejects
      // `office` outright with a 400 — so it gets neither an office chip nor a
      // separate region one.
      state: v.regions,
      dealer: v.dealers,
      partner: v.partners,
      finance: v.finance_statuses,
    }
    return SALES_FILTER_KEYS.flatMap((key) => {
      const values = flat[key]
      if (!values?.length || values.length > MAX_OPTIONS) return []
      return [{ id: key, label: FILTER_LABELS[key], options: [ALL_OPTION, ...values] }]
    })
  }

  const dimensions = catalogue.values.dimensions
  return SALES_FILTER_KEYS.flatMap((key) => {
    const dimension: FilterDimension | undefined = dimensions[key]
    if (!dimension?.values?.length) return []
    // Mostly-blank dimensions describe the data, not a way through it.
    if (typeof dimension.blank_pct === 'number' && dimension.blank_pct > 90) return []

    // Busiest first: the values worth filtering on are the ones with rows behind
    // them, and a 200-entry dropdown sorted by count puts those at the top.
    const options = [...dimension.values]
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_OPTIONS)
      .map((v) => v.value)

    return [
      {
        id: key,
        label: FILTER_LABELS[key],
        options: [ALL_OPTION, ...options],
        note: dimension.note,
      },
    ]
  })
}

/** Chip selections → the repeatable query params, with `All` meaning "unset". */
export function toFilterSelection(chosen: Record<string, string>): SalesFilterSelection {
  const selection: SalesFilterSelection = {}
  for (const key of SALES_FILTER_KEYS) {
    const value = chosen[key]
    if (value && value !== ALL_OPTION) selection[key] = [value]
  }
  return selection
}

/** Defaults every chip opens on, and what "Clear filters" restores. */
export function defaultFilterValues(groups: FilterGroup[]): Record<string, string> {
  return Object.fromEntries(groups.map((g) => [g.id, ALL_OPTION]))
}

/* ----------------------------------------------------- period sales (2026-09) */

/** "-32.2%" with the arrow the tile needs, or nothing when there is no comparison. */
function toDelta(
  metric: { delta_pct: number | null; previous: number | null } | undefined,
  label: string,
  previousWindow?: { from_date: string; to_date: string },
): StatTileData['delta'] {
  const pct = metric?.delta_pct
  if (typeof pct !== 'number') return undefined
  const span = previousWindow ? ` (${previousWindow.from_date} → ${previousWindow.to_date})` : ''
  return {
    value: `${Math.abs(Number(pct.toFixed(1)))}%`,
    direction: pct >= 0 ? 'up' : 'down',
    title: `${label}: ${naNumber(metric?.previous)} in the previous period${span}`,
  }
}

/**
 * The four headline tiles for a period.
 *
 * Contract value is dropped entirely on One Portal rather than shown as "n/a":
 * their API has no revenue at all, so the tile would be permanently empty, and a
 * column of nothing reads as a fault rather than as an absence.
 */
export function toPeriodTiles(summary: SalesSummary | undefined, unit: string): StatTileData[] {
  const previous = summary?.previous
  const tiles: StatTileData[] = [
    {
      id: 'period-sales',
      label: unit,
      value: naNumber(summary?.sales.value),
      // The expected band exists only for today; for any other period the note
      // carries the daily average, which is the figure that makes spans of
      // different lengths comparable at all.
      note: summary?.expected
        ? `/ Exp. ${summary.expected.min}-${summary.expected.max}`
        : typeof summary?.daily_avg === 'number'
          ? `${summary.daily_avg}/day`
          : undefined,
      tone: 'neutral',
      delta: toDelta(summary?.sales, 'Sales', previous),
    },
    {
      id: 'period-cancelled',
      label: 'Cancelled',
      value: naNumber(summary?.cancelled.value),
      tone: summary?.cancelled.value ? 'degraded' : 'neutral',
      delta: toDelta(summary?.cancelled, 'Cancelled', previous),
    },
    {
      id: 'period-kw',
      label: 'kW',
      value: naNumber(summary?.kw.value, (n) => Math.round(n).toLocaleString()),
      tone: 'neutral',
      delta: toDelta(summary?.kw, 'kW', previous),
    },
  ]

  if (summary && summary.contract_value.value === null && summary.sales.value !== null) {
    // One Portal: revenue is absent by nature of their API, not missing today.
    return tiles
  }

  return [
    ...tiles,
    {
      id: 'period-value',
      label: 'Contract Value',
      value: naNumber(summary?.contract_value.value, money),
      note: summary?.contract_value.unparsed_count
        ? `${summary.contract_value.unparsed_count} unparsed`
        : undefined,
      tone: 'neutral',
      delta: toDelta(summary?.contract_value, 'Contract value', previous),
    },
  ]
}

/**
 * The single sales tile on the three-tile pages: the chosen period's figure,
 * titled for that period.
 *
 * It reads off `sales/summary` rather than `sales/overview` so that picking
 * "This week" retitles the tile and shows the week — one figure that always
 * says which span it covers, instead of a tile permanently labelled "Today"
 * over a screen the selector has moved elsewhere.
 *
 * The red badge is the period's cancellations, which is the one thing the
 * dropped Cancelled tile carried that a reader would otherwise lose.
 */
export function toPeriodSalesTile(
  summary: SalesSummary | undefined,
  unit: string,
  period: SalesPeriodSelection,
  /**
   * True while the period's summary is in flight. It matters because a wide
   * period takes a few seconds and "n/a" means *unknown* on this screen — a
   * tile that says it for five seconds reports a gap in the data that is really
   * just a request still running.
   */
  isLoading = false,
): StatTileData {
  const window = summary?.period
  const expected = summary?.expected
  // Custom has no name of its own, so it is titled by the dates the server
  // resolved it to — and reads "Custom" only until they arrive.
  const span =
    period.key === 'custom'
      ? window
        ? `${window.from_date} → ${window.to_date}`
        : PERIOD_LABELS.custom
      : PERIOD_LABELS[period.key]
  const pending = isLoading && !summary
  const cancelled = summary?.cancelled.value
  // "insufficient history" is a real answer, not a zero: a band of nothing
  // reads as a target the team missed, so it never prints as "Exp. 0-0".
  const hasBand = typeof expected?.min === 'number' && typeof expected?.max === 'number'

  return {
    id: 'period-sales-tile',
    label: `${unit} ${span}`,
    value: pending ? '…' : naNumber(summary?.sales.value),
    // The expected band exists only for today; every wider period gets the
    // daily average instead, which is what makes spans of different lengths
    // comparable at all.
    note: hasBand
      ? `/ Exp. ${expected?.min}-${expected?.max}`
      : typeof summary?.daily_avg === 'number'
        ? `${summary.daily_avg}/day`
        : expected
          ? '/ no baseline'
          : undefined,
    // Only today's figure has a band to be judged against; for any other
    // period the number is the answer, not a pass or a fail.
    tone:
      !hasBand || typeof summary?.sales.value !== 'number'
        ? 'neutral'
        : summary.sales.value >= (expected?.min ?? 0)
          ? 'healthy'
          : 'degraded',
    delta: cancelled
      ? {
          value: String(cancelled).padStart(2, '0'),
          direction: 'down',
          title: `${cancelled} cancelled · ${span}`,
        }
      : undefined,
  }
}

/** "$31.1M", "$904k" — contract value at a glance. */
function money(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1000) return `$${Math.round(value / 1000)}k`
  return `$${Math.round(value)}`
}

/**
 * Series buckets → the timeline's points.
 *
 * A null bucket is **unknown, not zero** — before the source's coverage begins,
 * or in the future — so it is carried through as null and the chart leaves a gap
 * rather than drawing a floor that never happened.
 *
 * The baseline series is the period's own running mean. The API publishes no
 * baseline for a period (its weekday baseline is a per-hour figure for one day),
 * and borrowing that one would compare a month against a number built for a
 * Tuesday afternoon.
 */
export function toSeriesPoints(series: SalesSeries | undefined): TimelinePoint[] {
  const buckets = series?.buckets ?? []
  const known = buckets.map((b) => b.sales).filter((n): n is number => typeof n === 'number')
  const mean = known.length ? known.reduce((a, b) => a + b, 0) / known.length : 0

  let runningBaseline = 0
  return buckets.map((b) => {
    runningBaseline += mean
    return {
      label: b.label,
      actual: b.sales,
      baseline: Number(mean.toFixed(2)),
      cumulativeActual: b.cumulative_sales,
      cumulativeBaseline: Number(runningBaseline.toFixed(2)),
      partial: b.partial,
    }
  })
}

/** "Data since 2026-04-04" — shown only when the source starts mid-period. */
export function coverageNote(coverage: SalesCoverage | undefined): string | null {
  if (!coverage?.partial) return null
  const from = coverage.covered_from?.slice(0, 10)
  return from ? `Data since ${from}` : 'Partial coverage for this period'
}
